// "How do I choose?" guides for Steps 2 and 3 of the Train page. Web only for now: docs/DESKTOP_PORT_BACKLOG.md
// lists them for the desktop's train_model_dialog.py.

/** Step 2: what validation is, how to pick the split, and where its result shows. */
export function SplitGuide() {
  return (
    <details className="guide" data-testid="guide-split">
      <summary>How do I choose the split, and where do I see the validation result?</summary>
      <div className="guide-body">
        <p><b>What the split does.</b> The training images teach the model. The validation images are held back: the model never learns
          from them, so they show how it does on pictures it has not seen. Only images with reviewed annotations take part. The same
          images are always split the same way, so two runs can be compared fairly, and each class found in at least two
          images gets an example on the validation side.</p>
        <p><b>Background pictures.</b> Add photos of the station with none of your objects in it, and a few with a hand, a tool
          or packaging in view, and mark them <b>Empty</b> in the gallery. They train as background pictures: the model learns
          what "nothing here" looks like and stops firing on the desk, the conveyor or a hand. About 1 in 10 pictures, at
          least 5. The Train page says when a project has too few.</p>
        <p><b>How to choose.</b></p>
        <table>
          <tbody>
            <tr><th>Reviewed images</th><th>Suggested split</th></tr>
            <tr><td>Fewer than 200</td><td>85 / 15: keep as much as possible for training</td></tr>
            <tr><td>200 – 2,000</td><td>80 / 20 (the default)</td></tr>
            <tr><td>More than 2,000</td><td>90 / 10: 10% is already plenty to measure with</td></tr>
          </tbody>
        </table>
        <p className="example">Example: 100 images, of which only 6 show "damaged box". At 80 / 20, 20 images are held back, and at least
          one of the 6 is among them. But one or two examples give a shaky score for that class: annotate more images of rare classes
          before relying on it.</p>
        <p><b>Where the result shows.</b> Every 5 epochs, and after the last one, the model is scored on the validation images. In Step 4,
          the log says "Validation check: mAP50 = …" each time, and above it <b>mAP50</b> is the latest score and <b>best mAP50</b> the
          highest so far. The model you save or export
          (best.pt) is the one from the epoch with the best score, not necessarily the last.</p>
        <p><b>Reading mAP50</b> (0 to 1: how well the boxes found match yours), as a rough guide: above 0.8 is good; 0.5 – 0.8 is usable
          but more or more varied images will help; below 0.5 the model needs more data, or the annotations need checking.</p>
      </div>
    </details>
  );
}

/** Step 3: model size, image size and epochs. */
export function SettingsGuide() {
  return (
    <details className="guide" data-testid="guide-settings">
      <summary>How do I choose the model size, image size and epochs?</summary>
      <div className="guide-body">
        <p><b>Model size.</b> Bigger models can learn more, but train and run slower and need more images to pay off.</p>
        <table>
          <tbody>
            <tr><th>Size</th><th>Use it when</th></tr>
            <tr><td>n — nano</td><td>Start here. Edge devices (ctrlX, Hailo), real-time use, and datasets of up to about 1,000 images.</td></tr>
            <tr><td>s — small, m — medium</td><td>nano's score has stopped improving, objects are small, or classes look alike.</td></tr>
            <tr><td>l — large, x — extra large</td><td>Thousands of images, and accuracy matters more than speed.</td></tr>
          </tbody>
        </table>
        <p><b>Image size.</b> The model always works on a square picture of one size. Each image is scaled down (keeping its shape, with
          grey bars to fill the square) to that size, both in training and later when the model is used, so the size is part of the
          model. A larger size keeps small details visible but is slower; changing it later means training again. This page
          suggests a size from your own images (their size, and how small your smallest objects are) and selects it for you; pick
          another to override it.</p>
        <p className="example">Example: 4000 × 3000 photos of screws trained at 640 shrink each screw to a few pixels, so 960 or 1280 helps.
          640 × 480 camera frames gain nothing above 640. Rule of thumb: the smallest object should still be at least about 10 – 15 pixels
          across after scaling.</p>
        <p><b>Epochs.</b> One epoch is one pass through all the training images. Too few and the model has not finished learning; too many
          wastes time, and the model starts memorising the training images instead of learning what the objects look like (its
          validation score stops rising or drops).</p>
        <p className="example">1 epoch: the model has barely started; its detections are close to useless. 10,000 epochs: hours of training
          for no gain, and a model that does worse on new pictures. That is why this page allows 10 – 300.</p>
        <p>Leave <b>Choose for my images</b> on. What a model needs is a number of learning steps, and one epoch is more steps over
          many images than over few: 60 epochs over 40 images is 300 steps, which our own tests showed is a model that has found the
          objects but is not yet sure of them. The page works the passes out from your image count so every run gets enough. With
          <b>Stop early</b> on (the default), that number is a maximum: training stops once the validation score has stopped improving,
          never in the first half of the run, so it costs little. If the results say the score was still rising at the end, type a
          larger number. best.pt always keeps the best epoch.</p>
      </div>
    </details>
  );
}
